import { memo, type DragEvent, type JSX, type MouseEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Id } from '../../../core/model';
import { isSystemSourceKind, SYSTEM_ITEM_REFUSAL } from '../../../core/system-items';
import { Icon, iconForSourceKind, type IconName } from '../../components/Icon';
import { InlineEdit } from '../../components/InlineEdit';
import { useProject, useRuntime, useUiContext } from '../../context';
import { attempt } from '../../safe';
import { dropPositionAt, type DropPosition } from '../listMath';
import type { TreeRow } from './tree';

export const ITEM_DRAG_TYPE = 'application/x-studio-item';

export interface SourceRowProps {
  sceneId: Id;
  row: TreeRow;
  selected: boolean;
  renaming: boolean;
  drop: DropPosition | null;
  onRenameDone(): void;
  onSelect(id: Id, event: MouseEvent): void;
  onOpen(id: Id): void;
  onMenu(id: Id, x: number, y: number): void;
  onDragOverRow(id: Id, position: DropPosition | null): void;
  onDropRow(draggedId: Id, targetId: Id, position: DropPosition): void;
}

/** One line of the sources tree. Subscribes only to the fields it shows, never to the transform. */
export const SourceRow = memo(function SourceRow(props: SourceRowProps): JSX.Element | null {
  const { sceneId, row, selected, renaming, drop } = props;
  const { studio, ui } = useUiContext();
  const { commands, queries, registry } = studio;

  const info = useProject(
    useShallow((p) => {
      const item = queries.findItem(p, sceneId, row.id)?.item;
      if (!item) return null;
      if (item.type === 'source') {
        const source = p.sources[item.sourceId];
        return { visible: item.visible, locked: item.locked, name: source?.name ?? 'Missing source', kind: source?.kind ?? '', refId: item.sourceId };
      }
      if (item.type === 'scene') {
        return { visible: item.visible, locked: item.locked, name: p.scenes[item.sceneId]?.name ?? 'Missing scene', kind: '', refId: item.sceneId };
      }
      return { visible: item.visible, locked: item.locked, name: item.name, kind: '', refId: '' };
    }),
  );
  const status = useRuntime((r) => (row.type === 'source' && info ? r.sources[info.refId]?.status : undefined));
  if (!info) return null;
  // System layers (OMP Advertising) stay visible, locked and on top: no selection, drag or toggles.
  const system = row.type === 'source' && isSystemSourceKind(info.kind);

  const icon: IconName =
    row.type === 'group' ? 'folder' : row.type === 'scene' ? 'layers' : iconForSourceKind(info.kind, registry.getSource(info.kind)?.category);
  const error = status?.state === 'error' ? (status.error?.message ?? 'The source reported an error.') : null;

  const rename = (value: string): void => {
    attempt(studio, () => {
      if (row.type === 'source') commands.renameSource(info.refId, value);
      else if (row.type === 'scene') commands.renameScene(info.refId, value);
      else commands.renameGroup(sceneId, row.id, value);
    });
    props.onRenameDone();
  };

  const positionOf = (event: DragEvent<HTMLLIElement>): DropPosition => {
    const rect = event.currentTarget.getBoundingClientRect();
    return dropPositionAt(event.clientY - rect.top, rect.height, row.type === 'group');
  };

  const toggle = (patch: { visible?: boolean; locked?: boolean }) => (event: MouseEvent) => {
    event.stopPropagation();
    attempt(studio, () => commands.updateItem(sceneId, row.id, patch));
  };

  return (
    <li
      id={`item-row-${row.id}`}
      role="treeitem"
      aria-level={row.depth + 1}
      aria-selected={selected}
      aria-expanded={row.type === 'group' ? !row.collapsed : undefined}
      className={['ui-list-row source-row', selected ? 'selected' : '', info.visible ? '' : 'hidden-item', system ? 'system-item' : '', drop ? `drop-${drop}` : ''].join(' ')}
      title={system ? SYSTEM_ITEM_REFUSAL : undefined}
      data-system={system || undefined}
      draggable={!renaming && !system}
      onClick={(e) => (system ? e.stopPropagation() : props.onSelect(row.id, e))}
      onDoubleClick={() => props.onOpen(row.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!system) props.onMenu(row.id, e.clientX, e.clientY);
      }}
      onDragStart={(e) => {
        if (system) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData(ITEM_DRAG_TYPE, row.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes(ITEM_DRAG_TYPE)) return;
        e.preventDefault();
        props.onDragOverRow(row.id, positionOf(e));
      }}
      onDragLeave={() => props.onDragOverRow(row.id, null)}
      onDrop={(e) => {
        e.preventDefault();
        const dragged = e.dataTransfer.getData(ITEM_DRAG_TYPE);
        if (dragged) props.onDropRow(dragged, row.id, positionOf(e));
      }}
    >
      <span className="tree-indent" style={{ width: row.depth * 14 }} />
      {row.type === 'group' ? (
        <button
          type="button"
          className="tree-twisty"
          tabIndex={-1}
          aria-label={row.collapsed ? 'Expand group' : 'Collapse group'}
          onClick={(e) => {
            e.stopPropagation();
            ui.getState().toggleCollapsed(row.id);
          }}
        >
          <Icon name={row.collapsed ? 'chevronRight' : 'chevronDown'} size={12} />
        </button>
      ) : null}
      <span className="kind-icon">
        <Icon name={icon} />
      </span>
      {renaming ? (
        <InlineEdit initial={info.name} label="Item name" onSubmit={rename} onCancel={props.onRenameDone} />
      ) : (
        <span className="name ui-ellipsis">{info.name}</span>
      )}
      {status ? (
        <span
          className={`ui-status-dot ${status.state}`}
          role="img"
          aria-label={error ? `Error: ${error}` : `Source ${status.state}`}
          title={error ?? `Source ${status.state}`}
        />
      ) : null}
      <button
        type="button"
        className={info.visible ? 'ui-icon-btn on' : 'ui-icon-btn off'}
        tabIndex={-1}
        aria-label={info.visible ? `Hide ${info.name}` : `Show ${info.name}`}
        aria-pressed={info.visible}
        disabled={system}
        onClick={toggle({ visible: !info.visible })}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Icon name={info.visible ? 'eye' : 'eyeOff'} />
      </button>
      <button
        type="button"
        className={info.locked ? 'ui-icon-btn on' : 'ui-icon-btn off'}
        tabIndex={-1}
        aria-label={info.locked ? `Unlock ${info.name}` : `Lock ${info.name}`}
        aria-pressed={info.locked}
        disabled={system}
        onClick={toggle({ locked: !info.locked })}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Icon name={info.locked ? 'lock' : 'unlock'} />
      </button>
    </li>
  );
});
